import { injectable } from 'inversify';
import { intersection } from 'lodash-es';
import { DateTime } from 'luxon';
import * as yup from 'yup';
import {
    AccreditationsSlotId,
    CustomFieldWithConditionFragment,
    DelegationAccreditationSlotFragment,
    FormInfosQuery,
    FormPositionRanking,
    FormRegisterInput,
    FormRegisterSource,
    RegisterAccreditationDisplay,
    RegisterPositionDisplay,
    RegisterSlotDisplay,
    UserEmail,
    UserInfoFormFragment,
    UsersInfoInput
} from '../generated/types';
import { TranslationService } from '../services/translationService';
import { isNonEmptyArray } from '../util/array';
import { Emptyable } from '../util/emptyable';
import {
    AccreditationFieldFormElement,
    FieldFormElement,
    uniqAccreditationFieldFormElement
} from '../vo/fieldFormElement';
import { FieldInputService } from './fieldInput';
import { InputService } from './inputService';
import { UserInfoInputService } from './userInfoInput';
import { VolunteerRegistrationInputService } from './volunteerRegistrationInput';
import { VolunteerRegistrationSlotInputService } from './volunteerRegistrationSlotInput';

export interface IFormRegisterValues {
    formRegister: FormRegisterInput;
}

export interface IFormRegisterEmailValues {
    email: UserEmail;
    formRegister: FormRegisterInput;
}

interface IFormRegisterInputDefaultOptions {
    countryCode: Emptyable<string>;
    delegationAccreditations?: DelegationAccreditationSlotFragment[];
    eventStartDate?: DateTime;
    forOther: boolean;
    sendNotificationEmail: boolean;
    slotDisplay: RegisterSlotDisplay;
    team: Emptyable<NonNullable<FormInfosQuery['organization']['event']>['team']>;
}

@injectable()
export class FormRegisterInputService extends InputService {
    constructor(
        translationService: TranslationService,
        private fieldInputService: FieldInputService,
        private userInfoInputService: UserInfoInputService,
        private volunteerRegistrationInputService: VolunteerRegistrationInputService,
        private volunteerRegistrationSlotInputService: VolunteerRegistrationSlotInputService
    ) {
        super(translationService);
    }

    formRegisterInputDefault(
        source: FormRegisterSource,
        userInfo: Emptyable<UserInfoFormFragment> | UsersInfoInput,
        customFields: CustomFieldWithConditionFragment[],
        options: IFormRegisterInputDefaultOptions
    ): FormRegisterInput {
        return {
            forOther: options.forOther,
            positionsSlotsIds: options.team?.positionsSlotsIds ?? [],
            positionsIds: options.team?.positionsIds ?? [],
            positionsCategoriesIds: options.team?.positionsCategoriesIds ?? [],
            accreditationsSlotsIds: (options.delegationAccreditations ?? [])
                .filter((da) => da.isDefault)
                .map((da) => da.accreditationSlotId),
            userInfo: this.userInfoInputService.userInfoUpdateInputDefault(userInfo, customFields, {
                countryCode: options.countryCode
            }),
            sendNotificationEmail: options.sendNotificationEmail,
            slots:
                options.slotDisplay === RegisterSlotDisplay.Display
                    ? [
                          this.volunteerRegistrationSlotInputService.volunteersRegistrationsSlotInputDefault(
                              options.eventStartDate
                          )
                      ]
                    : (options.team?.slots ?? []),
            source
        };
    }

    formRegisterInformationsSchema(formElements: FieldFormElement[]) {
        return yup.object().shape({
            formRegister: yup.object().shape({
                userInfo: this.userInfoInputService.formUserInfoInputSchema(formElements)
            })
        });
    }

    formRegisterSlotsSchema(
        slotDisplay: RegisterSlotDisplay,
        eventStartDate: DateTime,
        eventEndDate: DateTime
    ) {
        return yup.object().shape({
            formRegister: yup.object().shape({
                ...this.volunteerRegistrationInputService.slotsFields(
                    slotDisplay,
                    eventStartDate,
                    eventEndDate
                )
            })
        });
    }

    formRegisterPositionsSchema(
        positionRanking: FormPositionRanking,
        positionDisplay: RegisterPositionDisplay
    ) {
        return yup.object().shape({
            formRegister: yup.object().shape({
                ...this.volunteerRegistrationInputService.wishedPositionsFields(
                    positionRanking,
                    positionDisplay
                )
            })
        });
    }

    formRegisterAccreditationsSchema(
        accreditationDisplay: RegisterAccreditationDisplay,
        selectedAccreditationsSlotsIds: AccreditationsSlotId[],
        formElements: AccreditationFieldFormElement[]
    ) {
        const uniFormElements = uniqAccreditationFieldFormElement(formElements);

        return yup.object().shape({
            formRegister: yup.object().shape({
                ...this.volunteerRegistrationInputService.accreditationsFields(
                    accreditationDisplay
                ),
                userInfo: yup.object().shape({
                    fields: yup.object().shape(
                        Object.fromEntries(
                            uniFormElements.map((formElement) => {
                                const isMandatory =
                                    formElement.isMandatory &&
                                    isNonEmptyArray(
                                        intersection(
                                            formElement.accreditationsSlotsIds,
                                            selectedAccreditationsSlotsIds
                                        )
                                    );
                                return [
                                    formElement.customField.slug,
                                    this.fieldInputService.fieldSchema(
                                        formElement.customField,
                                        isMandatory
                                    )
                                ];
                            })
                        )
                    )
                })
            })
        });
    }

    formRegisterSchema(
        formElements: FieldFormElement[],
        slotDisplay: RegisterSlotDisplay,
        eventStartDate: DateTime,
        eventEndDate: DateTime,
        positionRanking: FormPositionRanking,
        positionDisplay: RegisterPositionDisplay,
        accreditationDisplay: RegisterAccreditationDisplay
    ) {
        return yup.object().shape({
            email: yup
                .string()
                .required(this.t('l_e_mail_est_re_04856'))
                .email(this.t('l_e_mail_est_re_04856')),
            formRegister: yup.object().shape({
                userInfo: this.userInfoInputService.formUserInfoInputSchema(formElements),
                ...this.volunteerRegistrationInputService.slotsFields(
                    slotDisplay,
                    eventStartDate,
                    eventEndDate
                ),
                ...this.volunteerRegistrationInputService.wishedPositionsFields(
                    positionRanking,
                    positionDisplay
                ),
                ...this.volunteerRegistrationInputService.accreditationsFields(accreditationDisplay)
            })
        });
    }
}
